import { useMemo } from 'react';
import { EventInstanceDto, getSecondsToTicketsSalesStart, getTicketSalesStartDateTime } from '@sweatpals/shared';
import { useTimer } from '@sweatpals/react-ui-kit';
import { formatDuration, intervalToDuration } from 'date-fns';

/**
 * @summary Provide a simplified interface to properly handle events with delayed tickets start
 * ToDo: extend this hook with an additional logic, if we decide to migrate sales start to ticket level
 * @example In any component, that has enough information about an event
 * const { isPurchaseAllowed, timeLeftReadable } = useTimeToEventStart(eventData);
 *
 * //... when do render
 * <Button disable={!isPurchaseAllowed}>
 * 	Sales start in {timeLeftReadable}
 * <Button/>
 */
export const useTimeToEventStart = (eventData?: Pick<EventInstanceDto, 'additionalData'>, timerIntervalSec = 20): {
	salesStartDateTime: Date | null;
	hasSalesStart: boolean;
	secondsLeftToEventStart: number | null;
	isPurchaseAllowed: boolean;
	timeLeftReadable: string;
} => {
	const salesStartDateTime = useMemo(() => getTicketSalesStartDateTime(eventData), [eventData]);
	const initialTimeToSalesStart = useMemo(() => getSecondsToTicketsSalesStart(eventData), [eventData]);

	// We only use timer to trigger values recalculation
	useTimer(
		null,
		salesStartDateTime ? Math.round(initialTimeToSalesStart / timerIntervalSec) : null,
		false,
		timerIntervalSec * 1000
	);

	// For better precision, actual time left calculates on every rerender
	const secondsLeftToEventStart = getSecondsToTicketsSalesStart(eventData);

	return {
		salesStartDateTime,
		hasSalesStart: !!salesStartDateTime,
		secondsLeftToEventStart,
		isPurchaseAllowed: !salesStartDateTime || secondsLeftToEventStart <= 0,
		// Round to minutes, to avoid unnecessary renders
		timeLeftReadable: formatDuration(intervalToDuration({ start: 0, end: Math.round(secondsLeftToEventStart / 60) * 60 * 1000 })),
	};
};
